import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "../../shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalFooter } from "../../shared/wrappers/FixaModal/FixaModalFooter";
import FixaButton from "../../shared/wrappers/FixaButton/FixaButton";
import { AssignedUserType, UsersList } from "./UsersList/UsersList";
import { useMemo, useState } from "react";
import { User } from "../../shared/types/user";
import FixaSearch from "../../shared/wrappers/FixaSearch/FixaSearch";
import { useTranslation } from "react-i18next";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { StyledFixaModalBody } from "./TeamModal/StyledModalComponents";
import { useAddUserToTeam } from "../hooks/useTeamMutations";

interface AddUserModalProps {
  users?: User[];
  close: () => void;
  team?: { id: string; name: string };
  visible: boolean;
  storeId: string;
}

const AddUserModal = ({
  users,
  close,
  team,
  visible,
  storeId,
}: AddUserModalProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastNotification();
  const [searchQuery, setSearchQuery] = useState("");
  const { mutate: addUserToTeam, isPending } = useAddUserToTeam(storeId);

  const matchingUsers = useMemo(() => {
    if (!searchQuery) return users?.filter((user) => user.teamId !== team?.id);
    return users?.filter(
      (user) =>
        user.teamId !== team?.id &&
        user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, users]);

  const [selectedUser, setSelectedUser] = useState<AssignedUserType | null>(
    null
  );

  const selectedUserInformation = useMemo(() => {
    return users?.find((user) => user.uid === selectedUser?.assignedUserId);
  }, [selectedUser, users]);

  const handleSubmit = async () => {
    const toastValues = {
      string1: selectedUserInformation?.fullName,
      string2: team?.name || "",
    };
    try {
      await addUserToTeam({
        uid: selectedUserInformation?.uid || "",
        teamId: team?.id || "",
      });
      close();
      showToast(t("dynamicAddedToString", toastValues));
    } catch (error) {
      showToast(t("dynamicFailedAddedToString", toastValues));
      console.error("Error assigning user:", error);
    } finally {
      setSelectedUser(null);
      setSearchQuery("");
    }
  };

  return (
    <FixaModal visible={visible} handleCloseBtn={close}>
      <FixaSheets
        alignment="right"
        preserveAlignment={false}
        size="small"
        fullSize
        header={
          <FixaModalHeader
            title={t("addUserToTeamString", { teamName: team?.name || "" })}
            floating={false}
          />
        }
        footer={
          <FixaModalFooter>
            <FixaButton
              disabled={isPending || !selectedUser}
              type="primary"
              onClick={handleSubmit}
            >
              {t("dynamicAddToString", {
                string1:
                  selectedUserInformation?.fullName ||
                  t("userString").toLowerCase(),
                string2: team?.name || "",
              })}{" "}
              {isPending ? "..." : ""}
            </FixaButton>
          </FixaModalFooter>
        }
      >
        <StyledFixaModalBody>
          <FixaSearch
            id="assign-to-search"
            placeholder={t("searchCoworkerString")}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            onClear={() => {
              setSearchQuery("");
            }}
          />
          <UsersList
            matchingUsers={matchingUsers || []}
            isLoading={false}
            teams={[]}
            onAssignedChange={setSelectedUser}
          />
        </StyledFixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};

export default AddUserModal;
